import { useState, useEffect, useCallback, useMemo } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material"
import type { TextFieldProps } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Repeat, Search, FileText, User, AlertCircle, Info } from "lucide-react"
import { invoiceSchedulesApi } from "@crm-src/lib/invoice-schedules-api"
import { customersApi } from "@crm-src/lib/customers-api"
import api from "@crm-src/lib/api"
import type { CreateInvoiceScheduleData, InvoiceScheduleFrequency } from "@crm-src/types/invoice-schedule"
import { cadenceUnitLabel, type RecurringFrequency } from "@crm-src/lib/cadence"
import { CadencePresetChips } from "@crm-src/components/cadence-preset-chips"
import { WholeNumberField } from "@crm-src/components/whole-number-field"

interface InvoiceOption {
  id: string
  invoice_number: string
  total_amount: number
  customer_id?: string
  customer_name?: string
  customer_email?: string
  issue_date?: string
  has_file?: boolean
}

interface CustomerOption {
  id: string
  full_name: string
  company_name?: string
  email?: string
}

interface CreateRecurringScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  invoice?: InvoiceOption
  customerId?: string
  customerName?: string
  customerEmail?: string
}

export function CreateRecurringScheduleModal({
  isOpen,
  onClose,
  onSuccess,
  invoice: propInvoice,
  customerId: propCustomerId,
  customerName: propCustomerName,
  customerEmail: propCustomerEmail
}: CreateRecurringScheduleModalProps) {
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Customer and Invoice selection state
  const [customers, setCustomers] = useState<CustomerOption[]>([])
  const [invoices, setInvoices] = useState<InvoiceOption[]>([])
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("")
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null)
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceOption | null>(null)
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false)
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false)
  // `customerInput` is what the Autocomplete box shows (display); `customerSearch`
  // is the debounced server query. They're separate so MUI's internal "reset"
  // events (blur/select) can update the display without clobbering the query.
  const [customerInput, setCustomerInput] = useState("")
  const [customerSearch, setCustomerSearch] = useState("")

  // Form state
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [frequency, setFrequency] = useState<InvoiceScheduleFrequency>("monthly")
  const [interval, setInterval] = useState(1)
  const [dayOfWeek, setDayOfWeek] = useState(1) // Monday
  const [dayOfMonth, setDayOfMonth] = useState(1)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [maxOccurrences, setMaxOccurrences] = useState<number | undefined>()
  const [paymentTermsDays, setPaymentTermsDays] = useState(30)
  // Invoices bill in ARREARS: each occurrence is generated at the END of the
  // period it covers. 1 = the period's last day.
  const [generateDaysBeforePeriodEnd, setGenerateDaysBeforePeriodEnd] = useState(1)

  // Determine if we're in standalone mode (no props provided)
  const isStandaloneMode = !propInvoice && !propCustomerId

  // Fetch customers when modal opens in standalone mode.
  // `search` is passed in (not closed over) so this callback stays stable
  // across keystrokes — otherwise it recreates on every character, re-running
  // the reset effect below and wiping the search box (the "can't type" bug).
  const fetchCustomers = useCallback(async (search?: string) => {
    if (!isStandaloneMode) return

    setIsLoadingCustomers(true)
    try {
      const response = await customersApi.getAll({
        per_page: 100,
        ...(search ? { search } : {})
      })
      setCustomers(response.customers || [])
    } catch (error) {
      console.error('Failed to fetch customers:', error)
    } finally {
      setIsLoadingCustomers(false)
    }
  }, [isStandaloneMode])

  // Fetch invoices for selected customer
  const fetchInvoices = useCallback(async (customerId: string) => {
    if (!customerId) {
      setInvoices([])
      return
    }

    setIsLoadingInvoices(true)
    try {
      const response = await api.get('/invoices', { params: { customer_id: customerId, per_page: 100 } })
      const invoicesData = response.data?.data || response.data?.invoices || []
      setInvoices(invoicesData.map((inv: any) => ({
        id: inv.id,
        invoice_number: inv.invoice_number,
        total_amount: inv.total_amount || 0,
        customer_id: inv.customer_id,
        customer_name: inv.customer_name || inv.customer?.full_name,
        customer_email: inv.customer_email || inv.customer?.email,
        issue_date: inv.issue_date,
        has_file: inv.has_file || inv.invoice_file_url
      })))
    } catch (error) {
      console.error('Failed to fetch invoices:', error)
      setInvoices([])
    } finally {
      setIsLoadingInvoices(false)
    }
  }, [])

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      // If props provided, use them
      if (propInvoice) {
        setSelectedInvoice(propInvoice)
        setSelectedCustomerId(propCustomerId || propInvoice.customer_id || "")
      } else if (propCustomerId) {
        setSelectedCustomerId(propCustomerId)
        setSelectedInvoice(null)
        // Fetch invoices for this customer
        fetchInvoices(propCustomerId)
      } else {
        // Standalone mode - reset everything
        setSelectedCustomerId("")
        setSelectedCustomer(null)
        setSelectedInvoice(null)
        setCustomers([])
        setInvoices([])
        setCustomerInput("")
        setCustomerSearch("")
        // Fetch customers
        fetchCustomers()
      }

      setName(propInvoice ? `Recurring - ${propInvoice.invoice_number}` : "")
      setDescription("")
      setFrequency("monthly")
      setInterval(1)
      setDayOfWeek(1)
      setDayOfMonth(1)
      setStartDate(new Date().toISOString().split('T')[0])
      setEndDate("")
      setMaxOccurrences(undefined)
      setPaymentTermsDays(30)
    }
  }, [isOpen, propInvoice, propCustomerId, propCustomerName, propCustomerEmail, fetchCustomers, fetchInvoices])

  // Fetch invoices when customer selection changes
  useEffect(() => {
    if (isStandaloneMode && selectedCustomerId && isOpen) {
      fetchInvoices(selectedCustomerId)
      setSelectedInvoice(null) // Reset invoice selection when customer changes
    }
  }, [selectedCustomerId, isStandaloneMode, isOpen, fetchInvoices])

  // Update form when invoice is selected
  useEffect(() => {
    if (selectedInvoice) {
      setName(`Recurring - ${selectedInvoice.invoice_number}`)
    }
  }, [selectedInvoice])

  // Debounced customer search — re-query the server as the user types.
  useEffect(() => {
    if (isStandaloneMode && isOpen) {
      const timer = setTimeout(() => {
        fetchCustomers(customerSearch)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [customerSearch, isStandaloneMode, isOpen, fetchCustomers])

  const handleCustomerSelect = (customer: CustomerOption | null) => {
    setSelectedCustomer(customer)
    setSelectedCustomerId(customer?.id ?? "")
  }

  const handleInvoiceSelect = (invoiceId: string) => {
    const invoice = invoices.find(i => i.id === invoiceId)
    setSelectedInvoice(invoice || null)
  }

  const handleSubmit = async () => {
    const effectiveCustomerId = propCustomerId || selectedCustomerId
    const effectiveInvoice = propInvoice || selectedInvoice

    if (!effectiveCustomerId) {
      toast({
        title: "Error",
        description: "Please select a customer",
        variant: "destructive",
      })
      return
    }

    if (!effectiveInvoice) {
      toast({
        title: "Error",
        description: "Please select a source invoice to use as template",
        variant: "destructive",
      })
      return
    }

    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Please enter a schedule name",
        variant: "destructive",
      })
      return
    }

    if (!startDate) {
      toast({
        title: "Error",
        description: "Please select a start date",
        variant: "destructive",
      })
      return
    }

    try {
      setIsSubmitting(true)

      const data: CreateInvoiceScheduleData = {
        customer_id: effectiveCustomerId,
        source_invoice_id: effectiveInvoice.id,
        name: name.trim(),
        description: description.trim() || undefined,
        frequency,
        interval,
        day_of_week: frequency === 'weekly' ? dayOfWeek : undefined,
        day_of_month: ['monthly', 'quarterly', 'yearly'].includes(frequency) ? dayOfMonth : undefined,
        start_date: startDate,
        end_date: endDate || undefined,
        max_occurrences: maxOccurrences,
        payment_terms_days: paymentTermsDays,
        generate_days_before_period_end: generateDaysBeforePeriodEnd,
      }

      const response = await invoiceSchedulesApi.create(data)

      if (response.success) {
        toast({
          title: "Success",
          description: response.message || "Recurring schedule created successfully",
        })
        onSuccess?.()
        onClose()
      } else {
        toast({
          title: "Error",
          description: response.errors?.[0] || "Failed to create schedule",
          variant: "destructive",
        })
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const daysOfWeek = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
  ]

  const effectiveInvoice = propInvoice || selectedInvoice

  // Keep the selected customer in the options even when the current (possibly
  // search-filtered) result set no longer contains it, so the Autocomplete can
  // always render the chosen value without a "value not in options" warning.
  const customerOptions = useMemo(() => {
    if (selectedCustomer && !customers.some((c) => c.id === selectedCustomer.id)) {
      return [selectedCustomer, ...customers]
    }
    return customers
  }, [customers, selectedCustomer])

  return (
    <Dialog open={isOpen} onClose={() => onClose()} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: "90vh" } }}>
      <DialogTitle sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Repeat size={20} />
          Create Recurring Invoice Schedule
        </Box>
        <Typography component="span" sx={{ fontSize: 14, color: "text.secondary", fontWeight: 400 }}>
          Set up a recurring schedule to automatically generate and optionally send invoices.
          {effectiveInvoice && (
            <Box component="span" sx={{ display: "block", mt: 0.5, fontSize: 14, fontWeight: 500 }}>
              Based on: {effectiveInvoice.invoice_number} (${Number(effectiveInvoice.total_amount || 0).toFixed(2)})
            </Box>
          )}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 2 }}>
          {/* Customer & Invoice Selection (only in standalone mode) */}
          {isStandaloneMode && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.06), borderRadius: 1, border: 1, borderColor: (t) => alpha(t.palette.primary.main, 0.24) }}>
              <Typography component="h3" sx={{ fontSize: 14, fontWeight: 500, color: "primary.main", display: "flex", alignItems: "center", gap: 1 }}>
                <FileText size={16} />
                Select Source Invoice
              </Typography>
              <Typography sx={{ fontSize: 12, color: "primary.main" }}>
                Choose a customer and invoice to use as the template for recurring invoices.
              </Typography>

              {/* Customer Selection */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormLabel sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                  <User size={16} />
                  Customer *
                </FormLabel>
                {/* One control that is both a dropdown and a search box:
                    open it to browse customers, or type to filter (server-side
                    search, debounced via `customerSearch`). */}
                <Autocomplete
                  fullWidth
                  size="small"
                  options={customerOptions}
                  value={selectedCustomer}
                  inputValue={customerInput}
                  loading={isLoadingCustomers}
                  onChange={(_event, newValue) => handleCustomerSelect(newValue)}
                  onInputChange={(_event, value, reason) => {
                    // Always mirror the box; only the user's own typing (or
                    // clearing) drives the server query — MUI's "reset" on
                    // select/blur must not overwrite the search term.
                    setCustomerInput(value)
                    if (reason === "input" || reason === "clear") setCustomerSearch(value)
                  }}
                  filterOptions={(x) => x}
                  getOptionLabel={(customer) => customer.full_name}
                  isOptionEqualToValue={(option, val) => option.id === val.id}
                  noOptionsText="No customers found"
                  loadingText="Loading customers..."
                  renderOption={(props, customer) => (
                    <Box component="li" {...props} key={customer.id}>
                      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                        <Box component="span">{customer.full_name}</Box>
                        {customer.company_name && (
                          <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>{customer.company_name}</Box>
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => {
                    // AutocompleteRenderInputParams doesn't line up with
                    // TextFieldProps under exactOptionalPropertyTypes — narrow it
                    // (valid at runtime), same workaround as multi-user-selector.
                    const inputProps = params as TextFieldProps
                    return (
                      <TextField
                        {...inputProps}
                        size="small"
                        placeholder="Search customers..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box sx={{ color: "text.disabled", display: "flex" }}><Search size={16} /></Box>
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{
                          ...params.inputProps,
                          "data-testid": "schedule-customer-search",
                        }}
                      />
                    )
                  }}
                />
              </Box>

              {/* Invoice Selection */}
              {selectedCustomerId && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormLabel sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                    <FileText size={16} />
                    Source Invoice *
                  </FormLabel>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={selectedInvoice?.id || ""}
                    onChange={(e) => handleInvoiceSelect(e.target.value)}
                    disabled={isLoadingInvoices}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (value) => {
                        const selected = value as string
                        if (!selected) {
                          return <Box component="span" sx={{ color: "text.disabled" }}>{isLoadingInvoices ? "Loading invoices..." : "Select an invoice"}</Box>
                        }
                        const invoice = invoices.find(i => i.id === selected)
                        return invoice ? <Box component="span" sx={{ fontWeight: 500 }}>{invoice.invoice_number}</Box> : null
                      },
                    }}
                  >
                    {invoices.map((invoice) => (
                      <MenuItem key={invoice.id} value={invoice.id}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 2 }}>
                          <Box component="span" sx={{ fontWeight: 500 }}>{invoice.invoice_number}</Box>
                          <Box component="span" sx={{ color: "text.secondary" }}>${Number(invoice.total_amount || 0).toFixed(2)}</Box>
                          {invoice.has_file && (
                            <Box component="span" sx={{ fontSize: 12, color: "success.main" }}>PDF</Box>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                    {invoices.length === 0 && !isLoadingInvoices && (
                      <Box sx={{ px: 1, py: 2, textAlign: "center", fontSize: 14, color: "text.secondary" }}>
                        No invoices found for this customer
                      </Box>
                    )}
                  </TextField>
                  {invoices.length === 0 && !isLoadingInvoices && selectedCustomerId && (
                    <Alert severity="error" sx={{ mt: 1 }} icon={<AlertCircle size={16} />}>
                      This customer has no invoices. Please create an invoice first before setting up a recurring schedule.
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Show selected invoice info when using props */}
          {!isStandaloneMode && propInvoice && (
            <Box sx={{ p: 2, bgcolor: "surfaceSoft", borderRadius: 1, border: 1, borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14 }}>
                <Box sx={{ color: "text.disabled", display: "flex" }}><FileText size={16} /></Box>
                <Box component="span" sx={{ fontWeight: 500 }}>Source Invoice:</Box>
                <Box component="span">{propInvoice.invoice_number}</Box>
                <Box component="span" sx={{ color: "text.secondary" }}>•</Box>
                <Box component="span">${Number(propInvoice.total_amount || 0).toFixed(2)}</Box>
              </Box>
            </Box>
          )}

          {/* Basic Info */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography component="h3" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Schedule Details</Typography>

            <Box sx={{ display: "grid", gap: 2 }}>
              <TextField
                id="name"
                label="Schedule Name *"
                size="small"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Monthly Maintenance Invoice"
              />

              <TextField
                id="description"
                label="Description"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
              />
            </Box>
          </Box>

          {/* Frequency Settings */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography component="h3" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Recurrence Pattern</Typography>

            <Box>
              <FormLabel sx={{ display: "block", mb: 0.75, fontSize: 14, fontWeight: 500, color: "text.primary" }}>Billing cadence</FormLabel>
              <CadencePresetChips
                frequency={frequency as RecurringFrequency}
                interval={interval}
                onSelect={(freq, ivl) => {
                  setFrequency(freq)
                  setInterval(ivl)
                }}
              />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                select
                label="Frequency *"
                size="small"
                fullWidth
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as InvoiceScheduleFrequency)}
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>

              <Box>
                <FormLabel sx={{ display: "block", mb: 0.5, fontSize: 14, fontWeight: 500, color: "text.primary" }}>Every</FormLabel>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={interval}
                    onChange={(e) => setInterval(parseInt(e.target.value) || 1)}
                    inputProps={{ min: 1, max: 60 }}
                    sx={{ width: 80 }}
                  />
                  <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                    {cadenceUnitLabel(frequency as RecurringFrequency)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {frequency === 'weekly' && (
              <TextField
                select
                label="Day of Week"
                size="small"
                fullWidth
                value={dayOfWeek.toString()}
                onChange={(e) => setDayOfWeek(parseInt(e.target.value))}
              >
                {daysOfWeek.map((day) => (
                  <MenuItem key={day.value} value={day.value.toString()}>
                    {day.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {['monthly', 'quarterly', 'yearly'].includes(frequency) && (
              <TextField
                select
                label="Day of Month"
                size="small"
                fullWidth
                value={dayOfMonth.toString()}
                onChange={(e) => setDayOfMonth(parseInt(e.target.value))}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <MenuItem key={day} value={day.toString()}>
                    {day}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                id="startDate"
                label="Start Date *"
                type="date"
                size="small"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                id="endDate"
                label="End Date (Optional)"
                type="date"
                size="small"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                inputProps={{ min: startDate }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                id="maxOccurrences"
                label="Max Occurrences (Optional)"
                type="number"
                size="small"
                fullWidth
                value={maxOccurrences || ""}
                onChange={(e) => setMaxOccurrences(e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="Unlimited"
                inputProps={{ min: 1 }}
              />

              <TextField
                id="paymentTermsDays"
                label="Payment Terms (Days)"
                type="number"
                size="small"
                fullWidth
                value={paymentTermsDays}
                onChange={(e) => setPaymentTermsDays(parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
              />
            </Box>

            <WholeNumberField
              id="generateDaysBeforePeriodEnd"
              label="Generate (Days Before Period Ends)"
              size="small"
              fullWidth
              data-testid="generate-lead-days"
              value={generateDaysBeforePeriodEnd}
              onChange={setGenerateDaysBeforePeriodEnd}
              inputProps={{ max: 31 }}
              helperText="1 = the last day of each billing period"
            />
          </Box>

          {/* When invoices are generated + how they are handled */}
          <Box sx={{ display: "flex", gap: 1.5, p: 2, bgcolor: "surfaceSoft", border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Box component="span" sx={{ display: "flex", color: "primary.light", mt: "2px" }}><Info size={16} /></Box>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Each invoice covers one billing period and is generated at the END of that period — after the
              service has been delivered, never before. A review copy is emailed to finance. Nothing is sent to
              the customer automatically — review the generated invoice and send it manually if appropriate.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || (isStandaloneMode && (!selectedCustomerId || !selectedInvoice))}
          {...(isSubmitting ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}
        >
          Create Schedule
        </Button>
      </DialogActions>
    </Dialog>
  )
}
